import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Newspaper, Search, Trash2, Copy, Check, Download } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import toast from 'react-hot-toast';

const AdminSubscribersPage: React.FC = () => {
  const subscribers = useAdminStore((s) => s.subscribers);
  const removeSubscriber = useAdminStore((s) => s.removeSubscriber);

  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let items = [...(subscribers ?? [])].sort(
      (a, b) => new Date(b.subscribedAt).getTime() - new Date(a.subscribedAt).getTime(),
    );
    if (q) {
      items = items.filter((s) => s.email.includes(q));
    }
    return items;
  }, [subscribers, search]);

  const formatDate = (date: string): string =>
    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatTime = (date: string): string =>
    new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const handleCopyAll = (): void => {
    const emails = (subscribers ?? []).map((s) => s.email).join(', ');
    navigator.clipboard.writeText(emails);
    setCopied(true);
    toast.success(`${(subscribers ?? []).length} emails copied`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportCSV = (): void => {
    const header = 'Email,Subscribed Date\n';
    const rows = (subscribers ?? [])
      .map((s) => `${s.email},${new Date(s.subscribedAt).toISOString()}`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const handleRemove = (id: string, email: string): void => {
    removeSubscriber(id);
    toast.success(`Removed ${email}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Subscribers</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {(subscribers ?? []).length} newsletter subscriber{(subscribers ?? []).length !== 1 ? 's' : ''}
          </p>
        </div>
        {(subscribers ?? []).length > 0 && (
          <div className="flex gap-2 shrink-0">
            <button
              onClick={handleCopyAll}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black/[0.04] dark:bg-white/[0.06] text-[10px] font-bold uppercase tracking-wider text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white transition-colors"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copied' : 'Copy All'}
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black/[0.04] dark:bg-white/[0.06] text-[10px] font-bold uppercase tracking-wider text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white transition-colors"
            >
              <Download size={13} />
              CSV
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      {subscribers.length > 0 && (
        <div className="relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
            placeholder="Search by email..."
          />
        </div>
      )}

      {filtered.length > 0 ? (
        <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-[1fr_160px_100px_48px] gap-4 px-5 py-3 border-b border-black/[0.04] dark:border-white/[0.04]">
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25">Email</span>
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25">Date</span>
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25">Time</span>
            <span />
          </div>

          {/* Rows */}
          <AnimatePresence>
            {filtered.map((sub) => (
              <motion.div
                key={sub._id}
                layout
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-1 sm:grid-cols-[1fr_160px_100px_48px] gap-1 sm:gap-4 items-center px-5 py-3.5 border-b border-black/[0.02] dark:border-white/[0.02] last:border-b-0 hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-colors"
              >
                <span className="text-sm font-medium text-black/80 dark:text-white/80 truncate">
                  {sub.email}
                </span>
                <span className="text-[11px] text-black/40 dark:text-white/40">
                  {formatDate(sub.subscribedAt)}
                </span>
                <span className="text-[11px] text-black/30 dark:text-white/30">
                  {formatTime(sub.subscribedAt)}
                </span>
                <button
                  onClick={() => handleRemove(sub._id, sub.email)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-black/15 dark:text-white/15 hover:text-red-500 hover:bg-red-500/5 transition-all justify-self-end"
                >
                  <Trash2 size={14} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
            <Newspaper size={24} className="text-black/10 dark:text-white/10" />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
            {(subscribers ?? []).length === 0 ? 'No subscribers yet' : 'No subscribers match your search'}
          </p>
          {(subscribers ?? []).length === 0 && (
            <p className="text-[10px] text-black/15 dark:text-white/15 mt-1">
              Subscribers will appear here when visitors sign up for the newsletter
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSubscribersPage;
